/**
 * Tudo que esta aplicacao pede a API de atendimento.
 *
 * Um arquivo so, e nao um por tela, porque as telas se cruzam: o inbox precisa
 * de filas e agentes para transferir, a supervisao precisa de conversa, a tela
 * de links precisa de fila. Espalhar isso por modulo produziria o mesmo
 * endpoint escrito de tres formas ligeiramente diferentes.
 *
 * O cliente e o `window.API_ATENDIMENTO`, montado em config/globalProperties.js
 * a partir do /config. O token e o mesmo da plataforma -- quem o anexa e o
 * apiClient compartilhado.
 */
import { apiRequest } from '@/services/api/apiClient.js';

const cliente = () => window.API_ATENDIMENTO;

function pedir(method, url, { body = {}, params = {} } = {}) {
    return apiRequest({ client: cliente(), method, url, body, params });
}

/* ------------------------------- Sessao ------------------------------- */

/** Quem sou, o que posso, quais filas atendo. Primeira chamada da aplicacao. */
export const meuPerfil = () => pedir('get', '/api/me');

export const trocarStatus = (status) => pedir('put', '/api/me/status', { body: { status } });

/** Sinal de vida. Sem ele o roteador para de mandar conversa para este agente. */
export const ping = () => pedir('post', '/api/me/ping');

/* ------------------------------ Conversas ----------------------------- */

export const listarConversas = (filtros = {}) => pedir('get', '/api/conversations', { params: filtros });

export const verConversa = (id) => pedir('get', `/api/conversations/${id}`);

/**
 * So o que mudou depois da mensagem `depoisDe`. E o que a tela chama em laco
 * -- por isso devolve tambem o estado da conversa: transferencia e
 * encerramento acontecem sem mensagem nova.
 */
export const novidades = (id, depoisDe = 0) =>
    pedir('get', `/api/conversations/${id}/messages`, { params: { after_id: depoisDe } });

export const enviarMensagem = (id, conteudo, nota = false) =>
    pedir('post', `/api/conversations/${id}/messages`, { body: { content: conteudo, note: nota } });

export const assumirConversa = (id, agenteId = null) =>
    pedir('post', `/api/conversations/${id}/assign`, { body: agenteId ? { agent_id: agenteId } : {} });

export const transferirConversa = (id, { filaId = null, agenteId = null, motivo = null } = {}) =>
    pedir('post', `/api/conversations/${id}/transfer`, {
        body: { queue_id: filaId, agent_id: agenteId, reason: motivo },
    });

export const encerrarConversa = (id, motivo = null) =>
    pedir('post', `/api/conversations/${id}/resolve`, { body: { reason: motivo } });

export const reabrirConversa = (id) => pedir('post', `/api/conversations/${id}/reopen`);

export const marcarLida = (id) => pedir('post', `/api/conversations/${id}/read`);

/** `typing` enquanto se digita, `monitor` enquanto o supervisor acompanha. */
export const sinalizar = (id, tipo = 'typing') =>
    pedir('post', `/api/conversations/${id}/signal`, { body: { kind: tipo } });

export const puxarProxima = (filaId = null) =>
    pedir('get', '/api/conversations/next', { params: filaId ? { queue_id: filaId } : {} });

export const etiquetarConversa = (id, etiquetas) =>
    pedir('post', `/api/conversations/${id}/tags`, { body: { tag_ids: etiquetas } });

export const historicoDoContato = (contatoId) => pedir('get', `/api/contacts/${contatoId}/conversations`);

/* -------------------------------- Filas ------------------------------- */

export const listarFilas = () => pedir('get', '/api/queues');
export const verFila = (id) => pedir('get', `/api/queues/${id}`);
export const criarFila = (dados) => pedir('post', '/api/queues', { body: dados });
export const salvarFila = (id, dados) => pedir('put', `/api/queues/${id}`, { body: dados });
export const apagarFila = (id) => pedir('delete', `/api/queues/${id}`);

/* ------------------------------- Equipes ------------------------------ */

export const listarEquipes = () => pedir('get', '/api/teams');
export const verEquipe = (id) => pedir('get', `/api/teams/${id}`);
export const criarEquipe = (dados) => pedir('post', '/api/teams', { body: dados });
export const salvarEquipe = (id, dados) => pedir('put', `/api/teams/${id}`, { body: dados });
export const apagarEquipe = (id) => pedir('delete', `/api/teams/${id}`);

/* ------------------------------- Agentes ------------------------------ */

export const listarAgentes = (filtros = {}) => pedir('get', '/api/agents', { params: filtros });

/**
 * Cadastra o recorte de atendimento de alguem que ja e usuario da corporacao.
 *
 * Nao cria pessoa: `platform_user_id` e o id dela no liberty/auth, escolhido
 * na lista de usuarios da corporacao (ver `usuariosDaCorporacao`).
 */
export const criarAgente = (dados) => pedir('post', '/api/agents', { body: dados });
export const salvarAgente = (id, dados) => pedir('put', `/api/agents/${id}`, { body: dados });

/* -------------------------- Links de chatweb -------------------------- */

export const listarLinks = (filtros = {}) => pedir('get', '/api/links', { params: filtros });
export const verLink = (id) => pedir('get', `/api/links/${id}`);
export const criarLink = (dados) => pedir('post', '/api/links', { body: dados });
export const salvarLink = (id, dados) => pedir('put', `/api/links/${id}`, { body: dados });
export const apagarLink = (id) => pedir('delete', `/api/links/${id}`);

/** O botao "gerar convite" da tela de atendimento: link de uso unico. */
export const gerarConvite = (dados) => pedir('post', '/api/links/invite', { body: dados });

/* ------------------------------ Supervisao ---------------------------- */

export const panorama = () => pedir('get', '/api/supervision/overview');
export const aoVivo = (filtros = {}) => pedir('get', '/api/supervision/live', { params: filtros });
export const relatorio = (filtros = {}) => pedir('get', '/api/supervision/report', { params: filtros });

/* ------------------------------- Catalogo ----------------------------- */

export const respostasRapidas = (filaId = null) =>
    pedir('get', '/api/canned-responses', { params: filaId ? { queue_id: filaId } : {} });

export const salvarResposta = (dados) => pedir('post', '/api/canned-responses', { body: dados });
export const apagarResposta = (id) => pedir('delete', `/api/canned-responses/${id}`);
export const listarEtiquetas = () => pedir('get', '/api/tags');
export const salvarEtiqueta = (dados) => pedir('post', '/api/tags', { body: dados });

/* --------------------- Pessoas, do liberty/auth ----------------------- */

/**
 * Usuarios da corporacao ativa -- a lista que alimenta o "adicionar agente".
 *
 * Vai na API de **auth**, nao na daqui: o cadastro de pessoa e de la. Usa
 * `/corp/{id}/users` em vez de `/user` porque o segundo lista a plataforma
 * inteira, e o que interessa e quem pertence a esta corporacao.
 */
export const usuariosDaCorporacao = async () => {
    const corp = localStorage.getItem('corp');

    if (!corp) return [];

    const resposta = await apiRequest({
        client: window.API_AUTH,
        method: 'get',
        url: `/corp/${corp}/users`,
    });

    return resposta?.data ?? resposta ?? [];
};

/* ------------------- Entrada local de desenvolvimento ----------------- */

/**
 * So responde com ATENDIMENTO_LOGIN_LOCAL ligado na API. Existe para abrir a
 * aplicacao na maquina do desenvolvedor, onde nao ha admin emitindo ticket.
 */
export const agentesLocais = () =>
    apiRequest({ client: cliente(), method: 'get', url: '/api/dev/agents', sendToken: false });

export const entrarLocal = (login) =>
    apiRequest({
        client: cliente(),
        method: 'post',
        url: '/api/dev/login',
        body: { login },
        sendToken: false,
    });

/* ---------------------------- Chat interno ---------------------------- */

/** A lista da esquerda do chat interno: setores e colegas, com nao lidas. */
export const conversasInternas = () => pedir('get', '/api/internal/threads');

/** So o total de nao lidas -- e o que alimenta o numerinho do menu. */
export const naoLidasInternas = () => pedir('get', '/api/internal/threads/unread');

/** Os agentes com quem da para abrir conversa. */
export const colegas = () => pedir('get', '/api/internal/threads/peers');

/** Abre (ou reencontra) a conversa com um agente ou com um setor. */
export const abrirConversaInterna = ({ agenteId = null, equipeId = null } = {}) =>
    pedir('post', '/api/internal/threads', {
        body: agenteId ? { agent_id: agenteId } : { team_id: equipeId },
    });

/** Sem `depoisDe`, o fim da conversa; com ele, so o que veio depois. */
export const mensagensInternas = (id, depoisDe = 0) =>
    pedir('get', `/api/internal/threads/${id}/messages`, { params: depoisDe ? { after: depoisDe } : {} });

export const enviarInterna = (id, conteudo) =>
    pedir('post', `/api/internal/threads/${id}/messages`, { body: { content: conteudo } });

export const marcarInternaLida = (id) => pedir('post', `/api/internal/threads/${id}/read`);
